import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import SearchableDropdown from '../components/SearchableDropdown';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';

interface League {
  id: string;
  name: string;
}

interface Team {
  id: string;
  name: string;
  teamNumber: string;
  leagueId?: string;
}

interface FieldCollection {
  id: string;
  name: string;
}

interface BlackoutPeriod {
  id: string;
  startDate: string;
  endDate: string;
  reason?: string;
}

interface FieldEligibility {
  date: string;
  fieldIds: string[];
}

interface GameSchedule {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

interface Season {
  id: string;
  leagueId: string;
  name: string;
  startDate: string;
  endDate: string;
  blackoutPeriods: BlackoutPeriod[];
  fieldEligibility: FieldEligibility[];
  gameSchedule: GameSchedule[];
  teamIds: string[];
  fieldCollectionId?: string;
  isDraft?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const CreateSeason: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (user && !isAdminOrSuperAdmin(user.profileType)) {
      navigate('/leagues');
    }
  }, [user, navigate]);

  const [formData, setFormData] = useState({
    leagueId: '',
    name: '',
    startDate: '',
    endDate: '',
    fieldCollectionId: ''
  });
  const [leagues, setLeagues] = useState<League[]>([]);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [fieldCollections, setFieldCollections] = useState<FieldCollection[]>([]);
  const [fields, setFields] = useState<Array<{ id: string; name: string }>>([]);
  const [blackoutPeriods, setBlackoutPeriods] = useState<BlackoutPeriod[]>([]);
  const [fieldEligibility, setFieldEligibility] = useState<FieldEligibility[]>([]);
  const [gameSchedule, setGameSchedule] = useState<GameSchedule[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSeasonId, setDraftSeasonId] = useState<string | null>(null);
  const [existingSeasons, setExistingSeasons] = useState<Season[]>([]);
  const [draftSeasons, setDraftSeasons] = useState<Season[]>([]);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showDrafts, setShowDrafts] = useState(true);
  const [activeTab, setActiveTab] = useState<'basic' | 'teams' | 'schedule' | 'fields' | 'blackouts'>('basic');

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    fetchLeagues();
    fetchTeams();
    fetchFieldCollections();
    fetchSeasons();
    fetchDraftSeasons();
  }, []);

  useEffect(() => {
    // Check if there's a draftId in the URL query params
    const draftIdFromUrl = searchParams.get('draftId');
    if (draftIdFromUrl && draftSeasons.length > 0) {
      // Wait for drafts to be loaded before trying to load a specific draft
      loadDraft(draftIdFromUrl);
    } else if (formData.leagueId && !draftIdFromUrl) {
      loadDraft();
    }
  }, [formData.leagueId, searchParams, draftSeasons.length]);

  useEffect(() => {
    if (formData.fieldCollectionId) {
      fetchFieldsFromCollection(formData.fieldCollectionId);
    } else {
      fetchAllFields();
    }
  }, [formData.fieldCollectionId]);

  const fetchLeagues = async () => {
    try {
      const response = await api.get('/leagues', { params: { page: '1', limit: '100' } });
      const leaguesData = response.data.leagues || response.data;
      setLeagues(leaguesData.filter((l: League) => l && l.id && l.name));
    } catch (error) {
      console.error('Error fetching leagues:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await api.get('/teams', { params: { page: '1', limit: '100' } });
      const teamsData = response.data.teams || response.data;
      setAllTeams(teamsData.filter((t: Team) => t && t.id && t.name));
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchFieldCollections = async () => {
    try {
      const response = await api.get('/fields/collections/all');
      setFieldCollections(response.data || []);
    } catch (error) {
      console.error('Error fetching field collections:', error);
    }
  };

  const fetchAllFields = async () => {
    try {
      const response = await api.get('/fields', { params: { page: '1', limit: '100' } });
      const fieldsData = response.data.fields || response.data;
      setFields(fieldsData
        .filter((f: any) => f && f.id && f.name)
        .map((f: any) => ({ id: f.id, name: f.name }))
      );
    } catch (error) {
      console.error('Error fetching fields:', error);
    }
  };

  const fetchFieldsFromCollection = async (collectionId: string) => {
    try {
      const response = await api.get(`/fields/collections/${collectionId}`);
      setFields((response.data.fields || [])
        .filter((f: any) => f && f.id && f.name)
        .map((f: any) => ({ id: f.id, name: f.name }))
      );
    } catch (error) {
      console.error('Error fetching fields from collection:', error);
    }
  };

  const fetchSeasons = async () => {
    try {
      const response = await api.get('/seasons', { params: { page: '1', limit: '100' } });
      const seasonsData = response.data.seasons || response.data || [];
      setExistingSeasons(seasonsData.filter((s: Season) => !s.isDraft));
    } catch (error) {
      console.error('Error fetching seasons:', error);
    }
  };

  const fetchDraftSeasons = async () => {
    try {
      const response = await api.get('/seasons/drafts');
      const draftsData = response.data.drafts || [];
      setDraftSeasons(draftsData);
    } catch (error) {
      console.error('Error fetching draft seasons:', error);
    }
  };

  const loadDraft = async (draftId?: string) => {
    try {
      if (draftId) {
        // Load specific draft by ID
        const draftToLoad = draftSeasons.find(d => d.id === draftId);
        if (draftToLoad) {
          loadDraftData(draftToLoad);
        } else {
          // Draft not in local state, fetch it
          const response = await api.get(`/seasons/${draftId}`);
          loadDraftData(response.data);
        }
      } else {
        // Try to load draft for current league
        const response = await api.get(`/seasons/draft/${formData.leagueId}`);
        const draft = response.data;
        loadDraftData(draft);
      }
    } catch (error: any) {
      // No draft exists, which is fine
      if (error.response?.status !== 404) {
        console.error('Error loading draft:', error);
      }
    }
  };

  const loadDraftData = (draft: any) => {
    // Load draft data into form
    setFormData({
      leagueId: draft.leagueId,
      name: draft.name || '',
      startDate: draft.startDate || '',
      endDate: draft.endDate || '',
      fieldCollectionId: draft.fieldCollectionId || ''
    });
    setSelectedTeamIds(draft.teamIds || []);
    setBlackoutPeriods(draft.blackoutPeriods || []);
    setFieldEligibility(draft.fieldEligibility || []);
    setGameSchedule(draft.gameSchedule || []);
    setDraftSeasonId(draft.id);
    
    if (draft.fieldCollectionId) {
      fetchFieldsFromCollection(draft.fieldCollectionId);
    } else {
      fetchAllFields();
    }
    
    // Refresh drafts list after loading
    fetchDraftSeasons();
  };

  const copyFromSeason = (season: Season) => {
    setFormData({
      leagueId: formData.leagueId || season.leagueId,
      name: '',
      startDate: '',
      endDate: '',
      fieldCollectionId: season.fieldCollectionId || ''
    });
    setSelectedTeamIds(season.teamIds || []);
    setBlackoutPeriods(season.blackoutPeriods.map(bp => ({
      id: Date.now().toString() + Math.random(),
      startDate: bp.startDate,
      endDate: bp.endDate,
      reason: bp.reason
    })));
    setFieldEligibility(season.fieldEligibility.map(fe => ({
      date: fe.date,
      fieldIds: [...fe.fieldIds]
    })));
    setGameSchedule(season.gameSchedule.map(gs => ({
      dayOfWeek: gs.dayOfWeek,
      startTime: gs.startTime,
      endTime: gs.endTime
    })));
    setShowCopyModal(false);
    
    if (season.fieldCollectionId) {
      fetchFieldsFromCollection(season.fieldCollectionId);
    } else {
      fetchAllFields();
    }
  };

  const handleSaveDraft = async () => {
    if (!formData.leagueId) {
      setError('Please select a league first');
      return;
    }

    setError('');
    setSavingDraft(true);

    try {
      const response = await api.post('/seasons', {
        leagueId: formData.leagueId,
        name: formData.name,
        startDate: formData.startDate,
        endDate: formData.endDate,
        teamIds: selectedTeamIds,
        fieldCollectionId: formData.fieldCollectionId || undefined,
        blackoutPeriods: blackoutPeriods.filter(bp => bp.startDate && bp.endDate),
        fieldEligibility: fieldEligibility.filter(fe => fe.date && fe.fieldIds.length > 0),
        gameSchedule: gameSchedule,
        isDraft: true
      });

      setDraftSeasonId(response.data.id);
      alert('Draft saved successfully!');
      // Refresh drafts list
      fetchDraftSeasons();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeamIds(prev =>
      prev.includes(teamId)
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const addBlackoutPeriod = () => {
    setBlackoutPeriods([...blackoutPeriods, {
      id: Date.now().toString(),
      startDate: '',
      endDate: '',
      reason: ''
    }]);
  };

  const removeBlackoutPeriod = (id: string) => {
    setBlackoutPeriods(blackoutPeriods.filter(bp => bp.id !== id));
  };

  const updateBlackoutPeriod = (id: string, field: string, value: string) => {
    setBlackoutPeriods(blackoutPeriods.map(bp =>
      bp.id === id ? { ...bp, [field]: value } : bp
    ));
  };

  const addFieldEligibility = () => {
    setFieldEligibility([...fieldEligibility, {
      date: '',
      fieldIds: []
    }]);
  };

  const removeFieldEligibility = (index: number) => {
    setFieldEligibility(fieldEligibility.filter((_, i) => i !== index));
  };

  const updateFieldEligibility = (index: number, field: string, value: any) => {
    setFieldEligibility(fieldEligibility.map((fe, i) =>
      i === index ? { ...fe, [field]: value } : fe
    ));
  };

  const toggleFieldForDate = (dateIndex: number, fieldId: string) => {
    const fe = fieldEligibility[dateIndex];
    const newFieldIds = fe.fieldIds.includes(fieldId)
      ? fe.fieldIds.filter(id => id !== fieldId)
      : [...fe.fieldIds, fieldId];
    updateFieldEligibility(dateIndex, 'fieldIds', newFieldIds);
  };

  const addGameSchedule = () => {
    setGameSchedule([...gameSchedule, {
      dayOfWeek: 0,
      startTime: '09:00',
      endTime: '17:00'
    }]);
  };

  const removeGameSchedule = (index: number) => {
    setGameSchedule(gameSchedule.filter((_, i) => i !== index));
  };

  const updateGameSchedule = (index: number, field: string, value: any) => {
    setGameSchedule(gameSchedule.map((gs, i) =>
      i === index ? { ...gs, [field]: value } : gs
    ));
  };

  const handleSubmit = async (e: React.FormEvent, finalizeDraft = false) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (finalizeDraft && draftSeasonId) {
        // Finalize existing draft
        await api.post(`/seasons/${draftSeasonId}/finalize`);
        alert('Season finalized successfully!');
      } else {
        // Create new season (or update draft)
        await api.post('/seasons', {
          leagueId: formData.leagueId,
          name: formData.name,
          startDate: formData.startDate,
          endDate: formData.endDate,
          teamIds: selectedTeamIds,
          fieldCollectionId: formData.fieldCollectionId || undefined,
          blackoutPeriods: blackoutPeriods.filter(bp => bp.startDate && bp.endDate),
          fieldEligibility: fieldEligibility.filter(fe => fe.date && fe.fieldIds.length > 0),
          gameSchedule: gameSchedule,
          isDraft: false
        });
        alert('Season created successfully!');
      }
      navigate('/leagues');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create season');
    } finally {
      setLoading(false);
    }
  };

  if (!user || !isAdminOrSuperAdmin(user.profileType)) {
    return <div className="container">Access denied. Admin only.</div>;
  }

  // Get teams that are not in the selected league or are already selected
  const availableTeams = allTeams.filter(t => 
    !t.leagueId || t.leagueId === formData.leagueId || selectedTeamIds.includes(t.id)
  );

  return (
    <div className="container" style={{ maxWidth: '1000px' }}>
      {draftSeasons.length > 0 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2 style={{ margin: 0 }}>Draft Seasons ({draftSeasons.length})</h2>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowDrafts(!showDrafts)}
            >
              {showDrafts ? 'Hide' : 'Show'} Drafts
            </button>
          </div>
          {showDrafts && (
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
              {draftSeasons.map((draft) => {
                const league = leagues.find(l => l.id === draft.leagueId);
                return (
                  <div
                    key={draft.id}
                    className="card"
                    style={{
                      border: draftSeasonId === draft.id ? '2px solid #2E7D32' : '1px solid #E0E0E0',
                      cursor: 'pointer',
                      transition: 'transform 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    onClick={() => loadDraft(draft.id)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                      <div>
                        <h3 style={{ margin: '0 0 5px 0' }}>{draft.name || 'Untitled Draft'}</h3>
                        <p style={{ color: '#757575', fontSize: '14px', margin: 0 }}>
                          {league?.name || 'Unknown League'}
                        </p>
                      </div>
                      <span className="badge badge-warning">Draft</span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#757575' }}>
                      {draft.startDate && draft.endDate ? (
                        <p style={{ margin: '5px 0' }}>
                          {new Date(draft.startDate).toLocaleDateString()} - {new Date(draft.endDate).toLocaleDateString()}
                        </p>
                      ) : (
                        <p style={{ margin: '5px 0', fontStyle: 'italic' }}>Dates not set</p>
                      )}
                      <p style={{ margin: '5px 0' }}>
                        <strong>Teams:</strong> {draft.teamIds?.length || 0}
                      </p>
                      {draft.gameSchedule && draft.gameSchedule.length > 0 && (
                        <p style={{ margin: '5px 0' }}>
                          <strong>Schedule slots:</strong> {draft.gameSchedule.length}
                        </p>
                      )}
                      {draft.blackoutPeriods && draft.blackoutPeriods.length > 0 && (
                        <p style={{ margin: '5px 0' }}>
                          <strong>Blackouts:</strong> {draft.blackoutPeriods.length}
                        </p>
                      )}
                      {draft.updatedAt && (
                        <p style={{ margin: '5px 0', fontSize: '12px', color: '#9E9E9E' }}>
                          Last updated: {new Date(draft.updatedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ marginTop: '10px', width: '100%' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        loadDraft(draft.id);
                      }}
                    >
                      Continue Editing
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '20px' }}>
          <div>
            <h1 style={{ margin: 0 }}>Create New Season</h1>
            {draftSeasonId && (
              <p style={{ color: '#2E7D32', marginTop: '5px', marginBottom: 0 }}>
                📝 Draft saved - You can continue editing and save as draft anytime
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {formData.leagueId && (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCopyModal(true)}
                >
                  Copy from Season
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleSaveDraft}
                  disabled={savingDraft}
                >
                  {savingDraft ? 'Saving...' : 'Save as Draft'}
                </button>
              </>
            )}
          </div>
        </div>
        <p style={{ color: '#757575', marginBottom: '20px' }}>
          Create a new season for a league. Teams added to the season will automatically be assigned to the league.
          You can save your progress as a draft and continue later.
        </p>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #E0E0E0' }}>
          <button
            type="button"
            className={activeTab === 'basic' ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setActiveTab('basic')}
            style={{ marginBottom: '-1px' }}
          >
            Basic Info
          </button>
          <button
            type="button"
            className={activeTab === 'teams' ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setActiveTab('teams')}
            style={{ marginBottom: '-1px' }}
          >
            Teams
          </button>
          <button
            type="button"
            className={activeTab === 'schedule' ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setActiveTab('schedule')}
            style={{ marginBottom: '-1px' }}
          >
            Schedule
          </button>
          <button
            type="button"
            className={activeTab === 'fields' ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setActiveTab('fields')}
            style={{ marginBottom: '-1px' }}
          >
            Fields
          </button>
          <button
            type="button"
            className={activeTab === 'blackouts' ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setActiveTab('blackouts')}
            style={{ marginBottom: '-1px' }}
          >
            Blackouts
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {activeTab === 'basic' && (
            <div>
              <div className="form-group">
                <label>League *</label>
                <SearchableDropdown
                  options={leagues.map(l => ({ id: l.id, label: l.name, subtitle: '' }))}
                  value={formData.leagueId}
                  onChange={(value) => {
                    setFormData({ ...formData, leagueId: value });
                    setDraftSeasonId(null); // Reset draft when league changes
                    // Try to load draft for the new league
                    if (value) {
                      setTimeout(() => loadDraft(), 100);
                    }
                  }}
                  placeholder="Select a league..."
                  allowClear={false}
                />
                {formData.leagueId && (
                  <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {(() => {
                      const previousSeasons = existingSeasons.filter(s => s.leagueId === formData.leagueId);
                      const mostRecent = previousSeasons.sort((a, b) => {
                        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                        return dateB - dateA;
                      })[0];
                      
                      return mostRecent ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => copyFromSeason(mostRecent)}
                          style={{ fontSize: '12px' }}
                        >
                          Copy from Previous Season ({mostRecent.name})
                        </button>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Season Name {!draftSeasonId && '*'}</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required={!draftSeasonId}
                  placeholder="e.g., Spring 2025"
                />
                {draftSeasonId && (
                  <small style={{ color: '#757575' }}>Optional for drafts - required when finalizing</small>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label>Start Date {!draftSeasonId && '*'}</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    required={!draftSeasonId}
                  />
                </div>

                <div className="form-group">
                  <label>End Date {!draftSeasonId && '*'}</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    required={!draftSeasonId}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Field Collection (Optional)</label>
                <SearchableDropdown
                  options={fieldCollections.map(c => ({ id: c.id, label: c.name }))}
                  value={formData.fieldCollectionId}
                  onChange={(value) => setFormData({ ...formData, fieldCollectionId: value })}
                  placeholder="Select a field collection..."
                  allowClear={true}
                />
                <small style={{ color: '#757575' }}>
                  Selecting a collection will pre-populate available fields. You can still customize field eligibility by date.
                </small>
              </div>
            </div>
          )}

          {activeTab === 'teams' && (
            <div>
              <h3>Select Teams</h3>
              <p style={{ color: '#757575', marginBottom: '15px' }}>
                Teams selected here will automatically be assigned to the league. A team can only be in one league at a time.
              </p>
              {!formData.leagueId ? (
                <p style={{ color: '#9E9E9E' }}>Please select a league first in the Basic Info tab.</p>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #E0E0E0', borderRadius: '8px', padding: '10px' }}>
                  {availableTeams.length === 0 ? (
                    <p style={{ color: '#9E9E9E' }}>No teams available.</p>
                  ) : (
                    availableTeams.map(team => (
                      <label
                        key={team.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          marginBottom: '5px',
                          background: selectedTeamIds.includes(team.id) ? '#E8F5E9' : 'transparent'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedTeamIds.includes(team.id)}
                          onChange={() => handleTeamToggle(team.id)}
                          style={{ width: '20px', height: '20px', marginRight: '10px', cursor: 'pointer' }}
                        />
                        <div>
                          <strong>{team.name}</strong>
                          <span style={{ color: '#757575', marginLeft: '10px' }}>{team.teamNumber}</span>
                          {team.leagueId && team.leagueId !== formData.leagueId && (
                            <span className="badge badge-warning" style={{ marginLeft: '10px' }}>
                              Currently in another league
                            </span>
                          )}
                        </div>
                      </label>
                    ))
                  )}
                </div>
              )}
              {selectedTeamIds.length > 0 && (
                <p style={{ marginTop: '10px', color: '#757575' }}>
                  {selectedTeamIds.length} team(s) selected
                </p>
              )}
            </div>
          )}

          {activeTab === 'schedule' && (
            <div>
              <h3>Game Schedule</h3>
              <p style={{ color: '#757575', marginBottom: '15px' }}>
                Define which days of the week and times games can be played.
              </p>
              {gameSchedule.map((schedule, index) => (
                <div key={index} style={{ border: '1px solid #E0E0E0', borderRadius: '8px', padding: '15px', marginBottom: '15px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '15px', alignItems: 'end' }}>
                    <div className="form-group">
                      <label>Day of Week</label>
                      <select
                        value={schedule.dayOfWeek}
                        onChange={(e) => updateGameSchedule(index, 'dayOfWeek', parseInt(e.target.value))}
                        style={{ width: '100%' }}
                      >
                        {daysOfWeek.map((day, i) => (
                          <option key={i} value={i}>{day}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Start Time</label>
                      <input
                        type="time"
                        value={schedule.startTime}
                        onChange={(e) => updateGameSchedule(index, 'startTime', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div className="form-group">
                      <label>End Time</label>
                      <input
                        type="time"
                        value={schedule.endTime}
                        onChange={(e) => updateGameSchedule(index, 'endTime', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => removeGameSchedule(index)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              <button type="button" className="btn btn-secondary" onClick={addGameSchedule}>
                + Add Schedule Slot
              </button>
            </div>
          )}

          {activeTab === 'fields' && (
            <div>
              <h3>Field Eligibility by Date</h3>
              <p style={{ color: '#757575', marginBottom: '15px' }}>
                Specify which fields are available for matches on specific dates.
              </p>
              {fieldEligibility.map((fe, index) => (
                <div key={index} style={{ border: '1px solid #E0E0E0', borderRadius: '8px', padding: '15px', marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div className="form-group" style={{ flex: 1, marginRight: '15px' }}>
                      <label>Date</label>
                      <input
                        type="date"
                        value={fe.date}
                        onChange={(e) => updateFieldEligibility(index, 'date', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => removeFieldEligibility(index)}
                    >
                      Remove
                    </button>
                  </div>
                  <div>
                    <label>Available Fields</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px', marginTop: '10px' }}>
                      {fields.map(field => (
                        <label key={field.id} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={fe.fieldIds.includes(field.id)}
                            onChange={() => toggleFieldForDate(index, field.id)}
                            style={{ marginRight: '8px' }}
                          />
                          {field.name}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" className="btn btn-secondary" onClick={addFieldEligibility}>
                + Add Date/Field Rule
              </button>
            </div>
          )}

          {activeTab === 'blackouts' && (
            <div>
              <h3>Blackout Periods</h3>
              <p style={{ color: '#757575', marginBottom: '15px' }}>
                Define periods when no games can be scheduled (e.g., holidays, maintenance).
              </p>
              {blackoutPeriods.map(bp => (
                <div key={bp.id} style={{ border: '1px solid #E0E0E0', borderRadius: '8px', padding: '15px', marginBottom: '15px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto', gap: '15px', alignItems: 'end' }}>
                    <div className="form-group">
                      <label>Start Date</label>
                      <input
                        type="date"
                        value={bp.startDate}
                        onChange={(e) => updateBlackoutPeriod(bp.id, 'startDate', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date</label>
                      <input
                        type="date"
                        value={bp.endDate}
                        onChange={(e) => updateBlackoutPeriod(bp.id, 'endDate', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div className="form-group">
                      <label>Reason (Optional)</label>
                      <input
                        type="text"
                        value={bp.reason || ''}
                        onChange={(e) => updateBlackoutPeriod(bp.id, 'reason', e.target.value)}
                        placeholder="e.g., Holiday break"
                        style={{ width: '100%' }}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => removeBlackoutPeriod(bp.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              <button type="button" className="btn btn-secondary" onClick={addBlackoutPeriod}>
                + Add Blackout Period
              </button>
            </div>
          )}

          {error && <div className="alert alert-error">{error}</div>}

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
            {draftSeasonId ? (
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={(e) => handleSubmit(e, true)}
                disabled={loading || !formData.leagueId || !formData.name || !formData.startDate || !formData.endDate}
              >
                {loading ? 'Finalizing...' : 'Finalize Season'}
              </button>
            ) : (
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading || !formData.leagueId || !formData.name || !formData.startDate || !formData.endDate}
              >
                {loading ? 'Creating...' : 'Create Season'}
              </button>
            )}
            {formData.leagueId && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleSaveDraft}
                disabled={savingDraft}
              >
                {savingDraft ? 'Saving...' : 'Save as Draft'}
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/leagues')}>
              Cancel
            </button>
          </div>
        </form>

        {showCopyModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
            overflow: 'auto',
            padding: '20px'
          }}>
            <div className="card" style={{ maxWidth: '600px', width: '100%', maxHeight: '80vh', overflow: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Copy from Existing Season</h2>
                <button className="btn btn-secondary" onClick={() => setShowCopyModal(false)}>✕</button>
              </div>
              
              <p style={{ color: '#757575', marginBottom: '15px' }}>
                Select a season to copy its settings. You can modify any details after copying.
              </p>

              {existingSeasons.length === 0 ? (
                <p style={{ color: '#9E9E9E' }}>No existing seasons found.</p>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {existingSeasons.map(season => {
                    const league = leagues.find(l => l.id === season.leagueId);
                    return (
                      <div
                        key={season.id}
                        style={{
                          border: '1px solid #E0E0E0',
                          borderRadius: '8px',
                          padding: '15px',
                          marginBottom: '10px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                        onClick={() => copyFromSeason(season)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div>
                            <h4 style={{ margin: 0 }}>{season.name}</h4>
                            <p style={{ color: '#757575', margin: '5px 0', fontSize: '14px' }}>
                              {league?.name || 'Unknown League'}
                            </p>
                            <p style={{ color: '#757575', margin: '5px 0', fontSize: '12px' }}>
                              {season.startDate && season.endDate 
                                ? `${new Date(season.startDate).toLocaleDateString()} - ${new Date(season.endDate).toLocaleDateString()}`
                                : 'Dates not set'}
                            </p>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                              <span className="badge badge-info">{season.teamIds.length} teams</span>
                              {season.gameSchedule.length > 0 && (
                                <span className="badge badge-info">{season.gameSchedule.length} schedule slots</span>
                              )}
                              {season.blackoutPeriods.length > 0 && (
                                <span className="badge badge-warning">{season.blackoutPeriods.length} blackouts</span>
                              )}
                            </div>
                          </div>
                          <button className="btn btn-sm btn-primary">Copy</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateSeason;

